/*
 * @providesModule ErrorDialogModal
 *
 */

import React from 'react';
import AWSManager from 'AWSManager';
import {_m} from 'INTL';
import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';
import SkillzImage from 'SkillzImage';
import SKZConstants from 'SKZConstants';
import Label from 'Label';
import LoadingIndicator from 'LoadingIndicator';
import ModalNavigationController from 'ModalNavigationController';
import Optional from 'Optional';
import PropTypes from 'prop-types';
import PushButton from 'PushButton';

import {View, StyleSheet, TouchableWithoutFeedback, NativeModules, Platform} from 'react-native';

const {
  btnGradientTwo,
  btnGradientOne,
  btnTextColorOne,
  btnTextColorTwo,
  viewTertiaryColor,
  viewCornerRadiusPrimary,
  fontSizeH2,
  fontSizeH3,
  fontBold,
  textColorSecondary,
  fontSizeH4,
  fontSecondary,
} = SkillzStyle.fullStyles;

type ErrorDialogModalProps = {
  onDone: () => void;
  retryCallback: (arg1: any) => void;
  onCancel?: () => void;
  retryText?: string;
  hideCancel?: boolean;
  overrideNativeDialog?: boolean;
  message?: string | any;
  error: string | any;
  hideErrorIcon?: boolean;
  titleText?: string;
};

type ErrorDialogModalState = {
  isShowingNativeDialog: boolean;
};

export default class ErrorDialogModal extends React.Component<
  ErrorDialogModalProps,
  ErrorDialogModalState
> {
  static propTypes = {
    // callback that is executed when any button is pressed. It is reccommended to use this to dismiss the modal.
    onDone: PropTypes.func.isRequired,
    // callback that is executed when the retry button is pressed.
    retryCallback: PropTypes.func.isRequired,
    // callback that is executed when the cancel button is pressed.
    onCancel: PropTypes.func,
    retryText: PropTypes.string,
    hideCancel: PropTypes.bool,
    overrideNativeDialog: PropTypes.bool,
    message: PropTypes.string,
    error: PropTypes.object.isRequired,
    hideErrorIcon: PropTypes.bool,
    titleText: PropTypes.string,
  };

  constructor(props: ErrorDialogModalProps) {
    super(props);

    this.state = {
      isShowingNativeDialog: false,
    };
  }

  componentDidMount() {
    if (Platform.OS == 'android') {
      NativeModules.SkillzModule.isShowingNativeDialog((isShowingNativeDialog: boolean) => {
        this.setState({isShowingNativeDialog: isShowingNativeDialog});
        if (isShowingNativeDialog) {
          setTimeout(() => {
            this.props.retryCallback(this);
            this.props.onDone?.();
          }, SKZConstants.MS_PER_SEC);
        }
      });
    }
  }

  static displayWithRetry(
    onRetry: () => void,
    onCancel: () => void,
    message: string | null,
    error: any
  ) {
    const onDone = () => {
      ModalNavigationController.hideModal();
    };

    const errorModal = (
      <ErrorDialogModal
        retryCallback={onRetry}
        onDone={onDone}
        onCancel={onCancel}
        message={message}
        error={error}
      />
    );

    LoadingIndicator.hide();
    ModalNavigationController.showModal(errorModal);
  }

  _renderContent(): React.ReactNode {
    const messageDisplay =
      this.props.message || _m("Uh oh! Doesn't look like that worked. Please try again");

    AWSManager.trackEvent('SDK_DISPLAYED_ERROR', {result: JSON.stringify(this.props.error)});

    return (
      <View style={styles.outerContentBox}>
        <Optional test={!this.props.hideErrorIcon}>
          <View style={styles.errorImageContainer}>
            <SkillzImage
              style={styles.errorImage}
              resizeMode={'contain'}
              source={'ic_error_icon_skz.png'}
            />
          </View>
        </Optional>
        <Optional test={this.props.titleText}>
          <Label style={styles.titleText}>{this.props.titleText}</Label>
        </Optional>
        <Label style={styles.textStyle} numberOfLines={0}>
          {messageDisplay}
        </Label>
        <View style={styles.buttonContainer}>
          <Optional test={!this.props.hideCancel}>
            <PushButton
              onPress={() => {
                this.props.onDone?.();
                this.props.onCancel?.();
              }}
              style={styles.buttonStyle}
              cornerRadius={viewCornerRadiusPrimary.borderRadius}
              colors={btnGradientTwo}
              title={_m('CANCEL')}
              key={'cancel'}
              textStyle={styles.buttonTextTwo}
            />
          </Optional>
          <PushButton
            onPress={() => {
              this.props.onDone?.();
              this.props.retryCallback?.(this);
            }}
            style={this.props.hideCancel ? styles.singleButtonStyle : styles.buttonStyle}
            cornerRadius={viewCornerRadiusPrimary.borderRadius}
            colors={btnGradientOne}
            title={this.props.retryText || _m('RETRY')}
            key={'next'}
            textStyle={styles.buttonTextOne}
          />
        </View>
      </View>
    );
  }

  render(): React.ReactNode {
    if (this.state.isShowingNativeDialog && !this.props.overrideNativeDialog) {
      return <View />;
    }

    if (this.props.hideCancel) {
      return (
        <View style={styles.fillView}>
          <View style={styles.modalOverlay}>{this._renderContent()}</View>
        </View>
      );
    }

    return (
      <TouchableWithoutFeedback
        style={styles.fillView}
        onPress={() => {
          this.props.onDone?.();
          this.props.onCancel?.();
        }}
      >
        <View style={styles.modalOverlay}>
          <TouchableWithoutFeedback>{this._renderContent()}</TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    );
  }
}

const styles = StyleSheet.create({
  fillView: {
    flex: 1,
  },

  modalOverlay: {
    position: 'absolute',
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },

  outerContentBox: {
    width: 300,
    padding: 10,
    alignSelf: 'center',
    justifyContent: 'space-between',
    ...viewCornerRadiusPrimary,
    ...viewTertiaryColor,
  },

  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
  },

  singleButtonStyle: {
    width: 140,
  },

  buttonStyle: {
    flex: 1,
    margin: 5,
  },

  buttonTextOne: {
    ...fontSizeH3,
    ...btnTextColorOne,
    ...fontSecondary,
  },
  buttonTextTwo: {
    ...fontSizeH3,
    ...btnTextColorTwo,
    ...fontSecondary,
  },

  errorImageContainer: {
    justifyContent: 'center',
    flexDirection: 'row',
  },

  errorImage: {
    height: 50,
    width: 50,
  },

  titleText: {
    ...fontSecondary,
    ...Sizes.select({
      phone: fontSizeH3,
      tablet: fontSizeH2,
    }),
    ...textColorSecondary,
    textAlign: 'center',
  },

  textStyle: {
    marginVertical: 10,
    textAlign: 'center',
    ...fontSizeH4,
    ...fontBold,
    ...textColorSecondary,
  },
});
